import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Download,
  Trash2,
  Pencil,
  Link2,
  Unlink,
  Calendar,
  Store,
  DollarSign,
  FileText,
  Tag,
  Image as ImageIcon,
  Save,
  X,
  Loader2,
  Maximize2,
} from 'lucide-react';
import type { Receipt } from '@/lib/types';
import { useAppDispatch } from '@/features/hooks';
import { updateReceipt, deleteReceipt, unlinkReceipt, fetchReceipt } from '@/features/receipts/receiptsSlice';
import { useToast } from '@/hooks/use-toast';
import { useCurrency } from '@/hooks/useCurrency';
import ReceiptImageViewer from './ReceiptImageViewer';
import ReceiptViewerDialog from '@/components/transactions/ReceiptViewerDialog';
import ReceiptCategorySuggestion from './ReceiptCategorySuggestion';
import ReceiptShareDialog from './ReceiptShareDialog';
import ReceiptImageEditor from './ReceiptImageEditor';
import { useReceiptBlob } from '@/hooks/useReceiptBlob';
import api from '@/services/api';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DetailRow({ icon: Icon, label, children }: { icon: React.ElementType; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/60">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <div className="text-sm break-words">{children}</div>
      </div>
    </div>
  );
}

interface ReceiptDetailSheetProps {
  receipt: Receipt | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ReceiptDetailSheet({ receipt, open, onOpenChange }: ReceiptDetailSheetProps) {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { fmt } = useCurrency();

  const [isEditing, setIsEditing] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [editTags, setEditTags] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [shareIncludeAmount, setShareIncludeAmount] = useState(true);
  const [shareIncludeItems, setShareIncludeItems] = useState(true);
  const [shareIncludeNotes, setShareIncludeNotes] = useState(true);
  const [shareIncludeMerchant, setShareIncludeMerchant] = useState(true);
  const [receiptViewerOpen, setReceiptViewerOpen] = useState(false);

  // Image MIME types: fetch via authenticated blob URL (cookie auth required).
  // PDFs continue to use the token-bearing direct URL because object/embed
  // viewers can break with blob: schemes (Round-2 §6 deferral).
  const { url: blobThumbnailUrl, loading: blobLoading, error: blobError } = useReceiptBlob(
    receipt?._id ?? '',
    receipt?.mimeType ?? '',
  );

  if (!receipt) return null;

  const baseUrl = api.defaults.baseURL || '';
  const fileUrl = `${baseUrl}/receipts/${receipt._id}/file`;
  const isPdf = receipt.mimeType === 'application/pdf';
  const viewerSrc = isPdf ? fileUrl : blobThumbnailUrl ?? '';

  const startEditing = () => {
    setEditNotes(receipt.notes || '');
    setEditTags(receipt.tags?.join(', ') || '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const tags = editTags.split(',').map((t) => t.trim()).filter(Boolean);
      await dispatch(updateReceipt({ id: receipt._id, data: { notes: editNotes, tags } })).unwrap();
      setIsEditing(false);
      toast({ title: 'Receipt updated' });
    } catch {
      toast({ title: 'Failed to update receipt', variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteReceipt(receipt._id)).unwrap();
      setDeleteDialogOpen(false);
      onOpenChange(false);
      toast({ title: 'Receipt deleted' });
    } catch {
      toast({ title: 'Failed to delete receipt', variant: 'destructive' });
    }
  };

  const handleUnlink = async () => {
    try {
      await dispatch(unlinkReceipt(receipt._id)).unwrap();
      toast({ title: 'Receipt unlinked from transaction' });
    } catch {
      toast({ title: 'Failed to unlink receipt', variant: 'destructive' });
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = receipt.originalFilename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCategorize = async (category: string) => {
    setCategorizing(true);
    try {
      await api.post(`/receipts/${receipt._id}/categorize`, { category });
      dispatch(fetchReceipt(receipt._id));
      toast({ title: 'Category applied' });
    } catch {
      toast({ title: 'Failed to apply category', variant: 'destructive' });
    }
    setCategorizing(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto"
          data-testid="receipt-detail-sheet"
        >
          <SheetHeader className="pb-4">
            <SheetTitle className="break-words">
              {receipt.metadata.merchant || receipt.originalFilename}
            </SheetTitle>
            <SheetDescription>
              Uploaded {new Date(receipt.createdAt).toLocaleDateString()}
            </SheetDescription>
          </SheetHeader>

          {/* Suggestion banner */}
          {receipt.suggestedCategory && !receipt.categorizedAt && (
            <div className="mb-4">
              <ReceiptCategorySuggestion
                suggestion={receipt.suggestedCategory}
                onApply={handleCategorize}
                applying={categorizing}
              />
            </div>
          )}

          {/* Image viewer */}
          <div className="mb-4 h-64 sm:h-80">
            {!isPdf && (blobLoading || (!blobThumbnailUrl && !blobError)) ? (
              <div
                className="relative h-full overflow-hidden rounded-lg border bg-muted/20"
                data-testid="receipt-detail-viewer-loading"
              >
                <Skeleton className="absolute inset-0" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              </div>
            ) : !isPdf && (blobError || !blobThumbnailUrl) ? (
              <div
                className="flex h-full items-center justify-center rounded-lg border border-dashed bg-muted/20 p-4"
                data-testid="receipt-detail-viewer-error"
              >
                <div className="text-center">
                  <ImageIcon className="mx-auto h-8 w-8 text-muted-foreground" />
                  <p className="mt-2 text-sm text-muted-foreground">Image unavailable</p>
                </div>
              </div>
            ) : (
              <ReceiptImageViewer
                src={viewerSrc}
                alt={receipt.originalFilename}
                mimeType={receipt.mimeType}
                className="h-full"
                imageTestId="receipt-detail-viewer-image"
                zoomInTestId="receipt-detail-viewer-zoom-in"
                zoomOutTestId="receipt-detail-viewer-zoom-out"
                zoomResetTestId="receipt-detail-viewer-zoom-reset"
              />
            )}
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="min-h-[44px]"
              onClick={() => setReceiptViewerOpen(true)}
              data-testid="receipt-detail-viewer-fullscreen"
            >
              <Maximize2 className="mr-2 h-4 w-4" /> View Full Size
            </Button>
          </div>

          <Separator />

          {/* Metadata */}
          <div className="space-y-3 py-4">
            {receipt.metadata.merchant && (
              <DetailRow icon={Store} label="Merchant">
                {receipt.metadata.merchant}
              </DetailRow>
            )}
            {receipt.metadata.date && (
              <DetailRow icon={Calendar} label="Receipt Date">
                {new Date(receipt.metadata.date).toLocaleDateString()}
              </DetailRow>
            )}
            {receipt.metadata.total != null && (
              <DetailRow icon={DollarSign} label="Total">
                {fmt(receipt.metadata.total)}
              </DetailRow>
            )}
            {receipt.metadata.category && (
              <DetailRow icon={Tag} label="Category">
                <Badge variant="outline">{receipt.metadata.category}</Badge>
              </DetailRow>
            )}
            <DetailRow icon={ImageIcon} label="File Info">
              <span>{receipt.originalFilename}</span>
              <span className="ml-2 text-xs text-muted-foreground">
                {formatBytes(receipt.originalSize)} → {formatBytes(receipt.compressedSize)}
                <span className="ml-1">({Math.round(receipt.compressionRatio * 100)}% saved)</span>
              </span>
            </DetailRow>

            {/* Transaction link */}
            {receipt.transactionId && (
              <DetailRow icon={Link2} label="Linked Transaction">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono">{receipt.transactionId}</span>
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={handleUnlink}>
                    <Unlink className="mr-1 h-3 w-3" /> Unlink
                  </Button>
                </div>
              </DetailRow>
            )}

            {/* Editable notes & tags */}
            {isEditing ? (
              <div className="space-y-3 rounded-md border p-3">
                <div>
                  <label className="text-xs font-medium">Notes</label>
                  <Textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    rows={2}
                    className="mt-1"
                    data-testid="receipt-edit-notes"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium">Tags (comma-separated)</label>
                  <Input
                    value={editTags}
                    onChange={(e) => setEditTags(e.target.value)}
                    className="mt-1"
                    data-testid="receipt-edit-tags"
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} data-testid="receipt-save">
                    <Save className="mr-1 h-3 w-3" /> Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>
                    <X className="mr-1 h-3 w-3" /> Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {receipt.notes && (
                  <DetailRow icon={FileText} label="Notes">
                    {receipt.notes}
                  </DetailRow>
                )}
                {receipt.tags.length > 0 && (
                  <DetailRow icon={Tag} label="Tags">
                    <div className="flex flex-wrap gap-1">
                      {receipt.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </DetailRow>
                )}
              </>
            )}
          </div>

          <Separator />

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-4">
            <Button variant="outline" size="sm" className="min-h-[44px]" onClick={startEditing} data-testid="receipt-edit">
              <Pencil className="mr-2 h-4 w-4" /> Edit
            </Button>
            <Button variant="outline" size="sm" className="min-h-[44px]" onClick={handleDownload} data-testid="receipt-download">
              <Download className="mr-2 h-4 w-4" /> Download
            </Button>
            <Button variant="outline" size="sm" className="min-h-[44px]" onClick={() => setShareOpen(true)} data-testid="receipt-share">
              <Link2 className="mr-2 h-4 w-4" /> Share
            </Button>
            {receipt.mimeType !== 'application/pdf' && (
              <Button variant="outline" size="sm" className="min-h-[44px]" onClick={() => setEditorOpen(true)} data-testid="receipt-edit-image">
                <ImageIcon className="mr-2 h-4 w-4" /> Edit Image
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="min-h-[44px] text-destructive hover:bg-destructive/10"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="receipt-delete"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete receipt?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove &ldquo;{receipt.originalFilename}&rdquo; and its compressed image.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share dialog */}
      <ReceiptShareDialog
        receiptId={receipt._id}
        open={shareOpen}
        onOpenChange={setShareOpen}
        includeAmount={shareIncludeAmount}
        onIncludeAmountChange={setShareIncludeAmount}
        includeItems={shareIncludeItems}
        onIncludeItemsChange={setShareIncludeItems}
        includeNotes={shareIncludeNotes}
        onIncludeNotesChange={setShareIncludeNotes}
        includeMerchant={shareIncludeMerchant}
        onIncludeMerchantChange={setShareIncludeMerchant}
      />

      {/* Image editor */}
      {receipt.mimeType !== 'application/pdf' && (
        <ReceiptImageEditor
          open={editorOpen}
          onOpenChange={setEditorOpen}
          imageSrc={fileUrl}
          receiptId={receipt._id}
          onSaved={() => {
            dispatch(fetchReceipt(receipt._id));
          }}
        />
      )}

      <ReceiptViewerDialog
        open={receiptViewerOpen}
        onOpenChange={setReceiptViewerOpen}
        receiptId={receipt._id}
        metadata={{
          filename: receipt.originalFilename,
          size: receipt.originalSize,
          mimetype: receipt.mimeType,
          uploadedAt: receipt.createdAt,
        }}
      />
    </>
  );
}
